import { useEffect, useRef, useState } from 'react'
import { Button } from '../Button'

type Props = {
  plannedMs: number
  sessionStartMs: number | null
  elapsedBeforePauseMs: number
  paused: boolean
  onPause: () => void
  onResume: () => void
  onAddMinutes: (m: number) => void
  onRestart: () => void
}

const RING_RADIUS = 100
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS


// TODO(focus-mode-phase-2): unify with quotasBudgetStock §4.5 — see docs/plans/capacitor-focus-mode-phase-2.md
const coachingBandColor = (progress: number) => {
  if (progress >= 0.9) return 'var(--success)'
  if (progress >= 0.6) return 'color-mix(in srgb, var(--success) 70%, var(--surface))'
  if (progress >= 0.25) return 'var(--warning)'
  return 'var(--muted)'
}

const formatRemaining = (milliseconds: number) => {
  const totalSeconds = Math.ceil(Math.max(0, milliseconds) / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export const FocusModeTimer = ({
  plannedMs,
  sessionStartMs,
  elapsedBeforePauseMs,
  paused,
  onPause,
  onResume,
  onAddMinutes,
  onRestart,
}: Props) => {
  const [displayElapsedMs, setDisplayElapsedMs] = useState(elapsedBeforePauseMs)
  const firedRef = useRef(false)

  useEffect(() => {
    firedRef.current = false
  }, [sessionStartMs])

  useEffect(() => {
    if (paused) return
    let frameId = 0
    let lastDrawAt = 0
    const tick = (now: number) => {
      if (now - lastDrawAt >= 250) {
        lastDrawAt = now
        const effectiveElapsedMs = paused
          ? elapsedBeforePauseMs
          : elapsedBeforePauseMs + (sessionStartMs !== null ? Math.max(0, Date.now() - sessionStartMs) : 0)
        setDisplayElapsedMs(effectiveElapsedMs)
        if (effectiveElapsedMs >= plannedMs && !firedRef.current) {
          firedRef.current = true
          void (async () => {
            try {
              const { Haptics, ImpactStyle } = await import('@capacitor/haptics')
              await Haptics.impact({ style: ImpactStyle.Light })
            } catch { /* web/no-op */ }
          })()
        }
      }
      frameId = window.requestAnimationFrame(tick)
    }
    frameId = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frameId)
  }, [paused, plannedMs, sessionStartMs, elapsedBeforePauseMs])

  const effectiveElapsedMs = paused ? elapsedBeforePauseMs : displayElapsedMs
  const progress = plannedMs > 0 ? Math.min(1, Math.max(0, effectiveElapsedMs / plannedMs)) : 1
  const offset = RING_CIRCUMFERENCE * (1 - progress)
  const ringColor = coachingBandColor(progress)

  return (
    <section className="flex flex-col items-center gap-5" aria-label="Focus timer">
      <div className="relative grid h-[220px] w-[220px] place-items-center">
        <svg className="absolute inset-0 -rotate-90" width="220" height="220" viewBox="0 0 220 220" aria-hidden="true">
          <circle cx="110" cy="110" r={RING_RADIUS} fill="none" strokeWidth="10" style={{ stroke: 'color-mix(in srgb, var(--border-strong) 60%, transparent)' }} />
          <circle
            cx="110"
            cy="110"
            r={RING_RADIUS}
            fill="none"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={offset}
            style={{ stroke: ringColor, transition: 'stroke 220ms ease, stroke-dashoffset 220ms ease' }}
          />
        </svg>
        <div className="relative text-center">
          <strong className="block font-mono text-5xl font-black tracking-tight text-[var(--text-strong)]">
            {formatRemaining(plannedMs - effectiveElapsedMs)}
          </strong>
          <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--muted)]">
            {effectiveElapsedMs >= plannedMs ? "Time's up" : 'remaining'}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-2" aria-label="Timer controls">
        <Button variant="secondary" size="md" onClick={paused ? onResume : onPause}>
          {paused ? '▶ Resume' : '⏸ Pause'}
        </Button>
        <Button variant="tertiary" size="md" onClick={() => onAddMinutes(5)}>
          +5m
        </Button>
        <Button variant="tertiary" size="md" onClick={onRestart}>
          Restart
        </Button>
      </div>
    </section>
  )
}
